import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';

/**
 * Kullanım Koşulları sayfası
 * @returns {JSX.Element} Kullanım Koşulları sayfası
 */
const TermsOfService = () => {
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/calculator">
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  {t('common.back')}
                </Button>
              </Link>
              <h1 className="text-3xl font-bold">{t('terms.title')}</h1>
            </div>
          </div>
        </div>
      </header>
      
      <main className="flex-1 container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* Giriş */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.introduction_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {t('terms.introduction_content')}
              </p>
            </CardContent>
          </Card>

          {/* Hizmet Tanımı */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.service_definition_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t('terms.service_definition_content')}
                </p>
                
                <h4 className="font-semibold text-base">{t('terms.service_features_title')}</h4>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>{t('terms.feature_calculation')}</li>
                  <li>{t('terms.feature_product_selection')}</li>
                  <li>{t('terms.feature_data_storage')}</li>
                  <li>{t('terms.feature_reporting')}</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Kullanıcı Hesabı ve Kimlik Doğrulama */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.user_account_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t('terms.user_account_content')}
                </p>
                
                <h4 className="font-semibold text-base">{t('terms.account_responsibilities_title')}</h4>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>{t('terms.responsibility_accuracy')}</li>
                  <li>{t('terms.responsibility_security')}</li>
                  <li>{t('terms.responsibility_unauthorized')}</li>
                  <li>{t('terms.responsibility_compliance')}</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Hizmet Kullanımı */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.service_usage_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t('terms.service_usage_content')}
                </p>
                
                <h4 className="font-semibold text-base">{t('terms.prohibited_activities_title')}</h4>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>{t('terms.prohibited_unauthorized')}</li>
                  <li>{t('terms.prohibited_reverse_engineer')}</li>
                  <li>{t('terms.prohibited_commercial')}</li>
                  <li>{t('terms.prohibited_malicious')}</li>
                  <li>{t('terms.prohibited_data_extraction')}</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Veri ve Hesaplamalar */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.data_calculations_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t('terms.data_calculations_content')}
                </p>
                
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <h4 className="font-semibold text-yellow-800 mb-2">{t('terms.important_notice_title')}</h4>
                  <p className="text-sm text-yellow-700">
                    {t('terms.important_notice_content')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fikri Mülkiyet */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.intellectual_property_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {t('terms.intellectual_property_content')}
              </p>
            </CardContent>
          </Card>

          {/* Sorumluluk Reddi */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.disclaimer_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t('terms.disclaimer_content')}
                </p>
                
                <h4 className="font-semibold text-base">{t('terms.liability_limitations_title')}</h4>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>{t('terms.limitation_accuracy')}</li>
                  <li>{t('terms.limitation_availability')}</li>
                  <li>{t('terms.limitation_compatibility')}</li>
                  <li>{t('terms.limitation_damages')}</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Hizmet Değişiklikleri */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.service_changes_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {t('terms.service_changes_content')}
              </p>
            </CardContent>
          </Card>

          {/* Hesap Sonlandırma */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.termination_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t('terms.termination_content')}
                </p>
                
                <h4 className="font-semibold text-base">{t('terms.termination_conditions_title')}</h4>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>{t('terms.termination_violation')}</li>
                  <li>{t('terms.termination_misuse')}</li>
                  <li>{t('terms.termination_security')}</li>
                  <li>{t('terms.termination_legal')}</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Uygulanacak Hukuk */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.governing_law_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {t('terms.governing_law_content')}
              </p>
            </CardContent>
          </Card>

          {/* İletişim Bilgileri */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.contact_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {t('terms.contact_content')}
                </p>
                
                <div className="bg-muted p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">ADC Makine Sanayi ve Ticaret Limited Şirketi</h4>
                  <p className="text-sm text-muted-foreground">
                    E-posta: info@alasfire.com<br />
                    Website: www.alasfire.com<br />
                    Telefon: +90 (XXX) XXX XX XX
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Koşulların Değişikliği */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.changes_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {t('terms.changes_content')}
              </p>
            </CardContent>
          </Card>

          <Separator />
          
          {/* Son Güncelleme */}
          <div className="text-center text-sm text-muted-foreground">
            <p>{t('terms.last_updated')}: {new Date().toLocaleDateString('tr-TR')}</p>
            <p className="mt-2">{t('terms.version')}: 1.0</p>
          </div>
        </div>
      </main>
      
      <footer className="py-6 border-t">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-center text-muted-foreground">
            © {new Date().getFullYear()} ADC Makine Sanayi ve Ticaret Limited Şirketi. {t('app.footer_text')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService; 